import { useMediaQuery } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import backArrow from 'assets/images/back-arrow-white.svg';

import { media } from 'atoms/breakpoints/breakpoints';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

import AnnouncementBanner from 'components/AnnouncementBanner/AnnouncementBanner';
import ComponentButton from 'components/Button/ComponentButton';
import DesktopMenu from 'components/Header/Desktop';
import Hamburger from 'components/Header/HamburgerIcon';
import MobileMenu from 'components/Header/Mobile';

import type { ContentfulComponentHeader } from 'graphqlTypes';

interface HeaderProps extends BasicProps, ContentfulComponentHeader {
  slug: string;
  isHomePage?: boolean;
  removeLogoLink?: boolean;
}

const Header: FC<HeaderProps> = ({
  __typename,
  logo,
  elementMenu,
  callToAction,
  isHomePage,
  announcementBanner,
  removeLogoLink,
  ...props
}) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isScrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(-1);
  const [openMenuGroup, setOpenMenuGroup] = useState(null);
  const [parentOpen, setParentOpen] = useState(null);

  const isDesktop = useMediaQuery(media.lg);
  const headerContainerRef = useRef(null);
  const mobileHeaderHeight = '96px';

  const handleMenuClick = (menuIndex: number) => {
    if (!isDesktop && openMenu === menuIndex) {
      return setOpenMenu(-1);
    }

    setOpenMenu(menuIndex);
  };

  useClickAway(headerContainerRef, () => {
    isDesktop && setOpenMenu(-1);
  });

  useEffect(() => {
    const handleResize = () => setIsMobileNavOpen(false);
    const handleScroll = () => setScrolled(window.scrollY > 0);

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {announcementBanner && <AnnouncementBanner {...announcementBanner} />}
      <Container
        ref={headerContainerRef}
        position={`${isHomePage ? 'fixed' : 'sticky'}`}
        top="-1px"
        left="0"
        width="100%"
        zIndex={10}
        lg={{ position: 'sticky' }}
        {...props}
        id="header-container"
      >
        <Flex
          as="nav"
          position="relative"
          className="header-wrapper"
          backgroundColor={
            (isMobileNavOpen && isHomePage) || isScrolled
              ? 'navy-900'
              : `${isHomePage ? 'common-transparent' : 'navy-900'}`
          }
          borderBottom={isMobileNavOpen ? '1.5px solid #E8E8E8' : null}
          marginX="auto"
          boxShadow={isScrolled || isMobileNavOpen ? 'lg' : null}
          width="100%"
          borderRadius="0"
          overflowX="inherit"
          lg={{ backgroundColor: 'navy-900', boxShadow: 'xl' }}
          {...props}
        >
          <Container width="100%" marginX="auto">
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              textAlign="center"
              paddingX={24}
              paddingY={0}
              maxWidth="1320px"
              marginX="auto"
              minHeight="96px"
              gap="32px"
              md={{ paddingX: 20 }}
            >
              <Flex width="100%" maxWidth="160px" display="block">
                <Link href={removeLogoLink ? '' : '/'} display="flex" textDecoration="none">
                  <Image
                    image={logo as ImageData}
                    alt="FieldRoutes Logo"
                    maxWidth="100%"
                    marginX="auto"
                    xs={{ display: openMenu < 0 ? 'flex' : 'none' }}
                    lg={{ display: 'flex' }}
                  />
                </Link>
                <Text
                  display="flex"
                  fontSize="textSm"
                  fontWeight="bold"
                  textColor="common-white"
                  textDecoration="none"
                  onClick={() => {
                    setParentOpen(true);
                    setOpenMenu(-1);
                  }}
                  css={`
                    display: ${openMenu >= 0 ? 'flex' : 'none'};

                    @media (min-width: 992px) {
                      display: none;
                    }

                    &:hover {
                      cursor: pointer;
                    }
                  `}
                >
                  <img src={backArrow} alt="back-arrow" />
                  Back
                </Text>
              </Flex>
              <Flex width="100%" justifyContent="flex-end" alignItems="center" lg={{ justifyContent: 'space-between' }}>
                <DesktopMenu
                  elementMenu={elementMenu}
                  isMobileNavOpen={isMobileNavOpen}
                  handleMenuClick={handleMenuClick}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                />
                <MobileMenu
                  elementMenu={elementMenu}
                  mobileHeaderHeight={mobileHeaderHeight}
                  isMobileNavOpen={isMobileNavOpen}
                  openMenu={openMenu}
                  setIsMobileNavOpen={setIsMobileNavOpen}
                  setOpenMenu={setOpenMenu}
                  setOpenMenuGroup={setOpenMenuGroup}
                  setParentOpen={setParentOpen}
                  openMenuGroup={openMenuGroup}
                  parentOpen={parentOpen}
                />
                <Flex alignItems="center" gap="32px">
                  {callToAction && (
                    <Flex
                      alignItems="center"
                      gap="16px"
                      minWidth="125px"
                      justifyContent="flex-end"
                      sm={{ display: 'flex', justifyContent: 'space-between', minWidth: '185px' }}
                      xl={{ display: 'flex' }}
                      css={`
                        justify-content: flex-end;

                        .MuiButton-root {
                          font-size: 16px;
                          width: max-content !important;
                        }

                        .button-log-in {
                          display: flex !important;
                        }

                        @media (max-width: 575px) {
                          .button-log-in {
                            display: none !important;
                          }
                        }

                        ${media.lg} {
                          min-width: 130px;
                          margin-left: 20px;
                        }
                      `}
                    >
                      {callToAction.map(button => (
                        <ComponentButton
                          {...button}
                          key={button?.label}
                          className={`button-${button?.label?.replace(' ', '-')?.toLocaleLowerCase()}`}
                        />
                      ))}
                    </Flex>
                  )}
                  {elementMenu && (
                    <Flex lg={{ display: 'none' }}>
                      <Hamburger
                        isOpen={isMobileNavOpen}
                        onClick={() => {
                          setParentOpen(null);
                          setIsMobileNavOpen(!isMobileNavOpen);
                          setOpenMenu(-1);
                        }}
                        css={`
                          &:hover {
                            cursor: pointer;
                          }
                        `}
                      />
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Container>
        </Flex>
      </Container>
    </>
  );
};

export default Header;
